// Home's "With <friend>" buttons for rooms I made or joined by code
// (rooms.js noteMyRoom). Pure: the card hands in my noted rooms and the
// metas it has read.
import { formatTime, roomSeconds, roomPaused } from '@/engine/solving';

/** Ids of my noted rooms for one date (or Giant id), newest first. */
export function roomIdsFor(myRooms, date) {
  return Object.entries(myRooms || {})
    .filter(([, r]) => r?.date === date)
    .sort(([, a], [, b]) => (b.at || 0) - (a.at || 0))
    .map(([id]) => id);
}

const firstName = (name) => String(name || 'Someone').trim().split(/\s+/)[0];

/** "Sam", "Sam and Jo", "Sam, Jo and Kit". */
export function namesLine(names) {
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/**
 * How much of a room's grid is filled, as a whole percent (at least 1 once
 * anything is), or null for a room that has never had its count written
 * (store noteRoomFilled; Sky 2026-10-30: home showed only my own progress).
 */
export function roomPercent(meta) {
  if (!meta?.squares || meta.filled == null) return null;
  if (!meta.filled) return 0;
  return Math.min(100, Math.max(1, Math.floor((meta.filled / meta.squares) * 100)));
}

/** "34% · 6:12:40", "34% · paused at 6:12:40", "solved in 9:41:02". */
export function roomProgressLine(meta, now) {
  if (!meta) return '';
  if (meta.solvedAt) return `solved in ${formatTime(roomSeconds(meta, meta.solvedAt))}`;
  const clock = formatTime(roomSeconds(meta, now));
  const pct = roomPercent(meta);
  return [pct == null ? null : `${pct}%`, roomPaused(meta, now) ? `paused at ${clock}` : clock].filter(Boolean).join(' · ');
}

/**
 * The buttons: one per room still being solved (a solved one drops off -
 * its finish is already my own record, so the card's main button reopens
 * it), never a group's room (the group has its own button), and only once
 * its meta has loaded.
 */
export function roomButtons(ids, metas, myUid, now = Date.now()) {
  const out = [];
  for (const id of ids) {
    const meta = metas[id];
    if (!meta || meta.solvedAt || meta.groupId) continue;
    const others = Object.entries(meta.players || {})
      .filter(([uid]) => uid !== myUid)
      .sort(([, a], [, b]) => (a.joinedAt || 0) - (b.joinedAt || 0))
      .map(([, p]) => firstName(p.name));
    out.push({
      id,
      label: others.length ? `With ${namesLine(others)}` : `Our room · ${meta.code}`,
      progress: roomProgressLine(meta, now),
    });
  }
  return out;
}
